import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import type { Athlete, AthleteDetail, Coach, Group, Page } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FormError,
  Loading,
  Modal,
  PageHead,
  Pagination,
  SelectField,
  StatusBadge,
  TextField,
  useToast,
} from "../../components/ui";
import { formatDate } from "../../lib/format";
import { useAsync } from "../../lib/useAsync";
import { Link, navigate, useSearchParams } from "../../router/router";
import { t } from "../../i18n";

const PAGE_SIZE = 20;

export function AthletesPage({ orgId }: { orgId: string }) {
  const { can } = useAuth();
  const params = useSearchParams();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState(params.get("status") ?? "");
  const [coachId, setCoachId] = useState(params.get("treinador") ?? "");
  const [groupId, setGroupId] = useState(params.get("grupo") ?? "");
  const [withoutCoach, setWithoutCoach] = useState(params.get("sem_treinador") === "1");
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(params.get("novo") === "1");

  useEffect(() => {
    const tp = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(tp);
  }, [q]);
  useEffect(() => setPage(1), [debounced, status, coachId, groupId, withoutCoach, sort]);

  const list = useAsync(
    () =>
      api.get<Page<Athlete>>(`/orgs/${orgId}/athletes`, {
        q: debounced,
        status,
        coach_id: coachId,
        group_id: groupId,
        without_coach: withoutCoach,
        sort,
        page,
        page_size: PAGE_SIZE,
      }),
    [orgId, debounced, status, coachId, groupId, withoutCoach, sort, page],
  );
  const coaches = useAsync(() => api.get<Coach[]>(`/orgs/${orgId}/coaches`), [orgId]);
  const groups = useAsync(() => api.get<Group[]>(`/orgs/${orgId}/groups`), [orgId]);
  const canManage = can("athletes.manage");
  const filtersActive = !!(debounced || status || coachId || groupId || withoutCoach);

  return (
    <>
      <PageHead
        title={t("Atletas")}
        subtitle={canManage ? t("Cadastro, situação e responsáveis de cada atleta.") : t("Atletas sob sua responsabilidade.")}
        actions={
          canManage && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t("Cadastrar atleta")}
            </Button>
          )
        }
      />

      <div className="toolbar" role="search">
        <div className="grow">
          <TextField label={t("Buscar")} placeholder={t("Nome ou e-mail")} value={q} onChange={(e) => setQ(e.target.value)} type="search" />
        </div>
        <SelectField label={t("Situação")} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("Todas")}</option>
          <option value="active">{t("Ativos")}</option>
          <option value="paused">{t("Pausados")}</option>
          <option value="inactive">{t("Inativos")}</option>
        </SelectField>
        <SelectField label={t("Treinador")} value={withoutCoach ? "__none" : coachId}
          onChange={(e) => {
            const v = e.target.value;
            setWithoutCoach(v === "__none");
            setCoachId(v === "__none" ? "" : v);
          }}>
          <option value="">{t("Todos")}</option>
          <option value="__none">{t("Sem treinador")}</option>
          {coaches.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.full_name}
            </option>
          ))}
        </SelectField>
        <SelectField label={t("Grupo")} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
          <option value="">{t("Todos")}</option>
          {groups.data?.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </SelectField>
        <SelectField label={t("Ordenar por")} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="name">{t("Nome (A–Z)")}</option>
          <option value="-name">{t("Nome (Z–A)")}</option>
          <option value="-joined_at">{t("Ingresso mais recente")}</option>
          <option value="joined_at">{t("Ingresso mais antigo")}</option>
        </SelectField>
      </div>

      {list.loading && !list.data && <Loading />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {list.data && list.data.total === 0 && (
        <div className="panel">
          {filtersActive ? (
            <EmptyState title={t("Nenhum atleta com esses filtros")}>{t("Ajuste a busca ou limpe os filtros.")}</EmptyState>
          ) : (
            <EmptyState
              title={t("Nenhum atleta por aqui")}
              action={canManage && <Button variant="primary" onClick={() => setCreating(true)}>{t("Cadastrar atleta")}</Button>}
            >
              {canManage ? t("Cadastre o primeiro atleta da assessoria.") : t("Nenhum atleta foi atribuído a você ainda.")}
            </EmptyState>
          )}
        </div>
      )}
      {list.data && list.data.total > 0 && (
        <>
          <div className="table-wrap" aria-busy={list.loading}>
            <table className="data responsive">
              <thead>
                <tr>
                  <th>{t("Atleta")}</th>
                  <th>{t("Treinador")}</th>
                  <th>{t("Grupo")}</th>
                  <th>{t("Ingresso")}</th>
                  <th>{t("Acesso")}</th>
                  <th>{t("Situação")}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link className="row-link" to={`/org/${orgId}/atletas/${a.id}`}>
                        {a.full_name}
                      </Link>
                      <span className="sub">{a.email ?? t("sem e-mail")}</span>
                    </td>
                    <td data-label={t("Treinador")}>{a.coach?.name ?? <span className="muted">{t("Sem treinador")}</span>}</td>
                    <td data-label={t("Grupo")}>{a.group?.name ?? "—"}</td>
                    <td data-label={t("Ingresso")}>{formatDate(a.joined_at)}</td>
                    <td data-label={t("Acesso")}>
                      {a.has_login ? <Badge tone="ok">{t("Com login")}</Badge> : a.invitation_pending ? <Badge tone="warn">{t("Convite enviado")}</Badge> : <Badge>{t("Sem login")}</Badge>}
                    </td>
                    <td>
                      <StatusBadge status={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={list.data.total} onPage={setPage} />
        </>
      )}

      {canManage && (
        <CreateAthleteModal
          open={creating}
          orgId={orgId}
          coaches={coaches.data ?? []}
          groups={groups.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={(a) => {
            setCreating(false);
            navigate(`/org/${orgId}/atletas/${a.id}`);
          }}
        />
      )}
    </>
  );
}

function CreateAthleteModal({
  open,
  orgId,
  coaches,
  groups,
  onClose,
  onCreated,
}: {
  open: boolean;
  orgId: string;
  coaches: Coach[];
  groups: Group[];
  onClose: () => void;
  onCreated: (a: AthleteDetail) => void;
}) {
  const toast = useToast();
  const empty = { full_name: "", email: "", phone: "", birth_date: "", joined_at: "", coach_id: "", group_id: "" };
  const [form, setForm] = useState(empty);
  const [invite, setInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const set = (k: keyof typeof empty) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (open) {
      setForm(empty);
      setError(null);
      setInvite(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body: Record<string, unknown> = { full_name: form.full_name, send_invitation: invite && !!form.email };
    for (const k of ["email", "phone", "birth_date", "joined_at", "coach_id", "group_id"] as const) {
      if (form[k]) body[k] = form[k];
    }
    try {
      const created = await api.post<AthleteDetail>(`/orgs/${orgId}/athletes`, body);
      toast(created.invitation_pending ? t("Atleta cadastrado e convite enviado.") : t("Atleta cadastrado."));
      onCreated(created);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const f = error?.fieldErrors() ?? {};
  return (
    <Modal
      open={open}
      title={t("Cadastrar atleta")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" type="submit" form="create-athlete" busy={busy}>
            {t("Cadastrar")}
          </Button>
        </>
      }
    >
      <form id="create-athlete" className="form" onSubmit={submit} noValidate>
        {error && error.code !== "validation_error" && <FormError error={error} />}
        <TextField label={t("Nome completo")} required value={form.full_name} onChange={set("full_name")} error={f.full_name} autoFocus />
        <div className="form-grid">
          <TextField label={t("E-mail")} type="email" value={form.email} onChange={set("email")} error={f.email}
            hint={t("Opcional. Necessário para o atleta ter login.")} />
          <TextField label={t("Telefone")} type="tel" value={form.phone} onChange={set("phone")} error={f.phone} />
          <TextField label={t("Data de nascimento")} type="date" value={form.birth_date} onChange={set("birth_date")} error={f.birth_date} />
          <TextField label={t("Data de ingresso")} type="date" value={form.joined_at} onChange={set("joined_at")} error={f.joined_at}
            hint={t("Se vazio, hoje.")} />
          <SelectField label={t("Treinador responsável")} value={form.coach_id} onChange={set("coach_id")} error={f.coach_id}>
            <option value="">{t("Definir depois")}</option>
            {coaches.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
              </option>
            ))}
          </SelectField>
          <SelectField label={t("Grupo")} value={form.group_id} onChange={set("group_id")} error={f.group_id}>
            <option value="">{t("Sem grupo")}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </SelectField>
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={invite && !!form.email} disabled={!form.email} onChange={(e) => setInvite(e.target.checked)} />
          {t("Enviar convite para o atleta criar a senha")}
        </label>
      </form>
    </Modal>
  );
}
